import { useEffect, useMemo, useRef, useState } from "react";
import { generatePath, useLocation } from "react-router";
import { styled } from "@pigment-css/react";
import { useLocale, useTranslations } from "@acto/web-common/locales";
import { ArrowRightDownCircle, ArrowRightUpCircle, Chart6 } from "@acto/web-common/components";
import { TopBar } from "@acto/taskpane/field-sales-2/components/patterns/TopBar";
import type { FilterPopoverGroup } from "@acto/taskpane/field-sales-2/components/patterns/FilterPopover";
import { CustomerSection } from "@acto/taskpane/field-sales-2/components/patterns/CustomerSection";
import { useDebouncedSearchTerm } from "@acto/taskpane/core/utils/hooks/useDebouncedSearchTerm";
import { useDirectionalNavigate } from "@acto/taskpane/field-sales-2/utils/viewTransitions";
import type Customer from "@acto/taskpane/core/domain/Customer";
import { useContributeChatScope, customersToClients } from "@acto/taskpane/core/chat-envelope";
import { CLIENT_LIST_LIMIT, INCLUDE_CLIENT_DETAILS } from "@acto/taskpane/field-sales-2/utils/chatScope";
import {
  AnalyticsEvents,
  AnalyticsSourceScope,
  countSearchMatchFields,
  searchMatchFields,
  type OpenedSearchResult,
  useTrack,
  useViewTrackedOnce,
} from "@acto/taskpane/core/analytics";
import ActoRoutes from "@acto/taskpane/core/router/routes.config";
import { PeriodComparisonScope } from "@acto/taskpane/field-sales-2/components/patterns/PeriodComparisonScope/PeriodComparisonScope";
import { YEAR_TO_DATE_SELECTION } from "@acto/taskpane/field-sales-2/utils/periodSelection";
import { useHomeCustomerList } from "./useHomeCustomerList";
import { EMPTY_QUICK_FILTER, isQuickFilterEmpty, toggleQuickFilterKey, type QuickFilterState } from "./quickFilter";

const Page = styled("main")({
  flex: 1,
  width: "100%",
  display: "flex",
  flexDirection: "column",
  // Reserve clearance so the last card scrolls clear of the fixed bottom nav.
  paddingBottom: "var(--bottom-nav-opaque-height, 0px)",
});

const ALL_KEY = "all";

// Filter key order — used to resolve "first selected" deterministically
// regardless of click order in state. Labels are looked up via translations
// inside the component so they re-render on locale change.
const FILTER_ORDER = ["focus", "growth", "decline"] as const satisfies readonly (keyof QuickFilterState)[];

export function AllCustomers() {
  const t = useTranslations("fieldSalesTwo");
  const { locale } = useLocale();
  const navigate = useDirectionalNavigate();
  const location = useLocation();

  const quickFilters = useMemo<FilterPopoverGroup[]>(
    () => [
      {
        key: "all",
        items: [{ key: ALL_KEY, label: t("filters.all") }],
      },
      {
        key: "focus",
        items: [
          {
            key: "focus",
            label: t("filters.customersInFocus"),
            icon: <Chart6 />,
          },
        ],
      },
      {
        key: "trend",
        items: [
          {
            key: "growth",
            label: t("filters.growth"),
            icon: <ArrowRightUpCircle />,
          },
          {
            key: "decline",
            label: t("filters.decline"),
            icon: <ArrowRightDownCircle />,
          },
        ],
      },
    ],
    [t],
  );

  const filterLabelsByKey = useMemo(
    () => new Map(quickFilters.flatMap((g) => g.items.map((i) => [i.key, i.label] as const))),
    [quickFilters],
  );

  // Faceted quick filter: OR within the trend pair, AND across groups.
  // "Alle" is the derived empty state, not a stored key.
  const [quickFilter, setQuickFilter] = useState<QuickFilterState>(EMPTY_QUICK_FILTER);

  const isFilterEmpty = isQuickFilterEmpty(quickFilter);

  function handleToggle(key: string) {
    setQuickFilter((prev) => toggleQuickFilterKey(prev, key));
  }

  // The popover shows "Alle" checked exactly when nothing is selected.
  const selectedKeys = useMemo(() => {
    if (isFilterEmpty) return [ALL_KEY];
    return FILTER_ORDER.filter((k) => quickFilter[k]);
  }, [quickFilter, isFilterEmpty]);

  const activeSummary = useMemo(() => {
    if (isFilterEmpty) return undefined;
    const activeOrdered = FILTER_ORDER.filter((k) => quickFilter[k]);
    const firstKey = activeOrdered[0];
    if (firstKey == null) return undefined;
    return {
      firstLabel: filterLabelsByKey.get(firstKey) ?? firstKey,
      extraCount: activeOrdered.length - 1,
    };
  }, [quickFilter, isFilterEmpty, filterLabelsByKey]);

  const { displayValue, debouncedValue, setDisplayValue } = useDebouncedSearchTerm();

  const { customers, revenuePeriod, isLoading, isLoadingMore, hasMore, loadMore } = useHomeCustomerList({
    search: debouncedValue,
    quickFilter,
  });

  // Ambient context: contribute the rendered list plus the search/filter state that produced it.
  const customersFragment = {
    ...customersToClients(customers, {
      includeDetails: INCLUDE_CLIENT_DETAILS,
      locale,
      revenuePeriod,
      limit: CLIENT_LIST_LIMIT,
    }),
    search_term: debouncedValue || undefined,
    quick_filter: isFilterEmpty ? undefined : FILTER_ORDER.filter((key) => quickFilter[key]),
  };
  useContributeChatScope("customers", customersFragment);

  const track = useTrack();
  const lastTrackedSearchRef = useRef<string | null>(null);

  // Guards against a quick-filter re-render refiring tracking for a term already sent. Reset on
  // clear so retyping the SAME term after clearing the search box tracks it again as a new search.
  useEffect(() => {
    if (!debouncedValue) {
      lastTrackedSearchRef.current = null;
      return;
    }
    if (!isLoading && customers !== undefined && lastTrackedSearchRef.current !== debouncedValue) {
      lastTrackedSearchRef.current = debouncedValue;
      track(AnalyticsEvents.CUSTOMER_SEARCH, {
        searchTerm: debouncedValue,
        resultCount: customers.length,
        resultCountByMatchedField: countSearchMatchFields(customers),
      });
    }
  }, [debouncedValue, isLoading, customers, track]);

  useViewTrackedOnce(() => track(AnalyticsEvents.CUSTOMER_LIST_VIEWED, {}));

  const handleOpenCustomer = (customer: Customer) => {
    const index = customers?.findIndex((candidate) => candidate.id === customer.id) ?? -1;
    const searchResult: OpenedSearchResult | undefined = debouncedValue
      ? { matchFields: searchMatchFields(customer), position: index >= 0 ? index + 1 : undefined }
      : undefined;
    navigate(generatePath(ActoRoutes.CUSTOMER_DETAILS, { id: encodeURIComponent(customer.id) }), {
      state: {
        from: location.pathname,
        searchResult,
      },
    });
  };

  return (
    <AnalyticsSourceScope source="customers_page">
      <PeriodComparisonScope selection={YEAR_TO_DATE_SELECTION}>
        <Page>
          <TopBar
            title={t("home.title")}
            searchPlaceholder={t("home.searchPlaceholder")}
            filter={{
              groups: quickFilters,
              selectedKeys,
              onToggle: handleToggle,
              activeSummary,
            }}
            search={{ value: displayValue, onChange: setDisplayValue }}
          />
          <CustomerSection
            revenuePeriod={revenuePeriod}
            customers={customers}
            isLoading={isLoading}
            isLoadingMore={isLoadingMore}
            hasMore={hasMore}
            loadMore={loadMore}
            onOpenCustomer={handleOpenCustomer}
          />
        </Page>
      </PeriodComparisonScope>
    </AnalyticsSourceScope>
  );
}
